import { cx } from '@linaria/core';
import { useRef, useState, useEffect } from 'react';
import { generateSrcSet, isPlatformSafari, isPlatformNodeJS } from '@utils/common';
import { useActivityIndicator } from '../hooks/activity-indicator.js';
import { image, wrapper } from './postloaded-image.style.js';
export { postloadedImageSelectors } from './postloaded-image.style.js';
import { jsx } from 'react/jsx-runtime';

/**
 * renders an image that will fade in once fully loaded. In the case loading is
 * slow, then a placeholder will be displayed while loading continues.
 *
 * furthermore, if the image is (almost) immediately available, then it will be
 * displayed without fading is it is unnecessary in this case.
 */
function PostloadedImage({
  className,
  showImmediately,
  maxWidth,
  onReady,
  ...props
}) {
  const didMountRef = useRef(false);
  const internalRef = useRef(null);
  const ref = props.ref ?? internalRef;

  // We optimistically assume that the media is ready in the client (during
  // SSR). In case it isn't we set loaded to false in a clientSide effect.
  const [isLoaded, setLoaded] = useState(true);

  // In case the image loads after a very short delay then simply show it
  // immediately. Else, ensure that the placeholder is at least shown for a
  // little while before showing the image to avoid too many jumps at once.
  const placeholder = useActivityIndicator(!isLoaded, {
    delayMs: 75,
    durationMs: 150
  });

  // Keep track of whether or not the placeholder actually was displayed
  const hasShownPlaceholderRef = useRef(false);
  useEffect(() => {
    if (!isLoaded && !placeholder.isActive()) {
      hasShownPlaceholderRef.current = false;
    } else if (placeholder.isActive()) {
      hasShownPlaceholderRef.current = true;
    }
  }, [placeholder, isLoaded]);

  // In case the image has already been loaded, make sure to mark it as such.
  useEffect(() => {
    if (didMountRef.current) {
      return;
    }
    didMountRef.current = true;
    if (showImmediately || isImageCached({
      maxWidth,
      sizes: props.sizes,
      src: props.src
    })) {
      onReady?.(ref.current ?? undefined);
    } else {
      setLoaded(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref]);
  return /*#__PURE__*/jsx("span", {
    className: cx(wrapper, className, placeholder.isActive() && "withPlaceholder", isLoaded && "loaded"),
    children: /*#__PURE__*/jsx("img", {
      ...props,
      className: cx(image, hasShownPlaceholderRef.current && "withTransition", isLoaded && "loaded"),
      srcSet: props.src && props.sizes ? generateSrcSet(props.src, maxWidth) : undefined,
      ref: ref,
      alt: props.alt,
      loading: !isPlatformSafari() ? props.loading ?? "lazy" : props.loading,
      onLoad: () => {
        setLoaded(true);
        onReady?.(ref.current ?? undefined);
      }
    })
  });
}
function isImageCached(props) {
  if (!props.src || isPlatformNodeJS()) {
    return false;
  }
  const img = new Image();
  img.src = props.src;
  if (props.src && props.sizes) {
    img.sizes = props.sizes;
    img.srcset = generateSrcSet(props.src, props.maxWidth);
  }
  return img.complete;
}

export { PostloadedImage, isImageCached };
//# sourceMappingURL=postloaded-image.js.map
